/** @jsx jsx */
import { jsx } from '@emotion/core'
import { Cell, DropdownItem, Grid, HFlow, Icon, PagedTable, Text, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { DropdownButton } from 'components/dropdown'
import { useErrorHandler } from 'components/error'
import { CheckboxField, Form, FormDebouncedValueSpy, TextField } from 'components/form'
import { HLabel } from 'components/HLabel'
import { Cnpj, Cpf } from 'components/label'
import { confirm } from 'components/modals/confirm'
import { TableBox, usePagedTableProps } from 'components/table'
import { useInativarSistemaExternoMutation } from 'graphql/hooks.generated'
import { PageParams, SistemaExternoQueryInput, SistemasExternosQuery, TipoPessoaEnum } from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'

type SistemaExternoQueryModel = SistemasExternosQuery['sistemasExternos']['content'][0]

interface SistemasExternosTableProps {
  readonly data: SistemasExternosQuery
  readonly loading: boolean
  readonly setFilter: (filter: SistemaExternoQueryInput) => void
  readonly refetch: () => void
}

export function SistemasExternosTable(props: Readonly<SistemasExternosTableProps>) {
  const alert = useAlert()
  const handleRejection = useErrorHandler()

  const [inativarSistemaExterno] = useInativarSistemaExternoMutation()
  const { data, loading, refetch, setFilter } = props

  const onInativarSistemaSuccess = () => {
    refetch()
    alert('success', 'Sistema externo inativado com sucesso.')
  }

  const onInativarSistema = (item: SistemaExternoQueryModel) =>
    confirm({
      title: 'Deseja inativar o sistema externo?',
      body: <InativarSistemaModalBody item={item} />,
      type: 'danger',
      confirmLabel: 'Inativar',
      onConfirm: () =>
        inativarSistemaExterno({ variables: { id: item.id } })
          .then(() => {
            onInativarSistemaSuccess()
          })
          .catch(handleRejection),
    })()

  const tableProps = usePagedTableProps<SistemasExternosQuery['sistemasExternos'], { pageParams?: PageParams }>({
    loading,
    result: data?.sistemasExternos,
    onChange: setFilter,
  })

  const columns = [
    {
      name: 'nome',
      header: 'Nome',
      render: (item: SistemaExternoQueryModel) => <Text>{item.nome}</Text>,
      width: '20%',
    },
    {
      name: 'responsavel',
      header: 'Responsável',
      render: renderResponsavel,
      width: '23%',
    },
    {
      name: 'url',
      header: 'URL',
      render: (item: SistemaExternoQueryModel) => <Text>{item.url}</Text>,
      width: '32%',
    },
    {
      name: 'localAcesso',
      header: 'Local de acesso',
      render: (item: SistemaExternoQueryModel) => <Text>{'Atendimento'}</Text>,
      width: '12%',
    },
    {
      name: 'status',
      header: 'Status',
      render: renderStatus,
      width: '6%',
    },
    {
      name: 'actions',
      header: '',
      render: (item: SistemaExternoQueryModel) => (
        <DropdownButton>
          <DropdownItem onClick={() => onInativarSistema(item)} disabled={!item.ativo}>
            Inativar
          </DropdownItem>
        </DropdownButton>
      ),
      width: '5%',
    },
  ]

  return (
    <TableBox header={<TableHeader onChange={setFilter} />}>
      <PagedTable
        loading={loading}
        rows={loading ? [] : data?.sistemasExternos?.content}
        columns={columns}
        {...tableProps}
      />
    </TableBox>
  )
}

const filterPath = metaPath<any>()

const TableHeader = ({ onChange }: { onChange: (values: any) => void }) => {
  return (
    <Form
      render={() => (
        <form noValidate>
          <FormDebouncedValueSpy onChange={({ values }) => onChange(values)} />
          <Grid gap={1}>
            <Cell size={5} alignSelf='center'>
              <TextField
                name={filterPath.query}
                icon='zoomOutline'
                placeholder='Pesquise por nome do sistema, responsável ou URL'
              />
            </Cell>
            <Cell size={7} alignSelf='center'>
              <CheckboxField name={filterPath.somenteAtivos} label='Exibir somente sistemas ativos' />
            </Cell>
          </Grid>
        </form>
      )}
    />
  )
}

const renderResponsavel = (item: SistemaExternoQueryModel) => (
  <VFlow vSpacing={0} style={{ margin: '0.5rem 0' }}>
    <Text>{item.nomeResponsavel}</Text>
    {item.tipoPessoa === TipoPessoaEnum.FISICA ? (
      <HLabel title='CPF'>
        <Cpf value={item.cpfCnpj} />
      </HLabel>
    ) : (
      <HLabel title='CNPJ'>
        <Cnpj value={item.cpfCnpj} />
      </HLabel>
    )}
    <Text>{item.email}</Text>
  </VFlow>
)

const InativarSistemaModalBody = ({ item }: { item: SistemaExternoQueryModel }) => (
  <VFlow>
    <Text>
      Ao inativar o sistema externo a ação não poderá ser desfeita. Caso necessário, após a inativação cadastre
      novamente o sistema.
    </Text>
    <VFlow vSpacing={0}>
      <HLabel title='Nome do sistema'>{item.nome}</HLabel>
      <HLabel title='Responsável'>{item.nomeResponsavel}</HLabel>
      {item.tipoPessoa === TipoPessoaEnum.FISICA ? (
        <HLabel title='CPF'>
          <Cpf value={item.cpfCnpj} />
        </HLabel>
      ) : (
        <HLabel title='CNPJ'>
          <Cnpj value={item.cpfCnpj} />
        </HLabel>
      )}
      <HLabel title='URL'>{item.url}</HLabel>
      <HLabel title='Local de acesso'>Atendimento</HLabel>
    </VFlow>
  </VFlow>
)

const renderStatus = ({ ativo }: SistemaExternoQueryModel) => (
  <HFlow hSpacing={0.25} alignItems='center'>
    <Icon size={1} fill={ativo ? 'success' : 'danger'} icon={ativo ? 'checkCircleOutline' : 'banOutline'} />
    <Text color={ativo ? 'success' : 'danger'}>{ativo ? 'Ativo' : 'Inativo'}</Text>
  </HFlow>
)
